import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2 } from 'lucide-react';
import { useRegions } from '@/hooks/useRegions';
import { useCopyChannelsBetweenRegions } from '@/hooks/useChannels';
import { useCategories } from '@/hooks/useCategories';

interface CopyChannelsModalProps {
  isOpen: boolean;
  onClose: () => void;
  providerId: number;
  currentVersionId: number;
  currentRegionId?: number;
}

export default function CopyChannelsModal({
  isOpen,
  onClose,
  providerId,
  currentVersionId,
  currentRegionId
}: CopyChannelsModalProps) {
  const [selectedRegionId, setSelectedRegionId] = useState<number | undefined>(undefined);
  const [selectedVersionId, setSelectedVersionId] = useState<number | undefined>(undefined);
  const [copyMode, setCopyMode] = useState<'all' | 'non-local' | 'local-only' | 'selected' | 'by-category'>('all');
  const [selectedChannelIds, setSelectedChannelIds] = useState<number[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  
  // Fetch regions
  const { data: regions = [] } = useRegions(providerId);
  
  // Filter out the current region
  const availableRegions = regions.filter(region => region.id !== currentRegionId);
  
  // Fetch versions for the selected region
  const { data: versions = [] } = useQuery({
    queryKey: ['region-versions', selectedRegionId],
    queryFn: async () => {
      if (!selectedRegionId) return [];
      
      const { supabase } = await import('@/lib/supabase');
      const { data, error } = await supabase
        .from('provider_versions')
        .select('id, version_number, status')
        .eq('provider_id', providerId)
        .eq('region_id', selectedRegionId)
        .order('version_number', { ascending: false });
        
      if (error) throw error;
      return data;
    },
    enabled: !!selectedRegionId,
  });
  
  // Fetch categories for the selected version
  const { data: categories = [], isLoading: isLoadingCategories } = useCategories(
    providerId,
    selectedVersionId || 0
  );
  
  // Fetch channels for the selected version
  const { data: channels = [], isLoading: isLoadingChannels } = useQuery({
    queryKey: ['copy-channels', selectedVersionId],
    queryFn: async () => {
      if (!selectedVersionId) return [];
      
      const { supabase } = await import('@/lib/supabase');
      const { data, error } = await supabase
        .from('channels')
        .select('id, name, is_local, category_id')
        .eq('version_id', selectedVersionId)
        .order('name');
        
      if (error) throw error;
      return data;
    },
    enabled: !!selectedVersionId && (copyMode === 'selected' || copyMode === 'by-category'),
  });
  
  // Reset selected version when region changes
  useEffect(() => {
    setSelectedVersionId(undefined);
  }, [selectedRegionId]);
  
  // Reset selected channels and categories when version changes
  useEffect(() => {
    setSelectedChannelIds([]);
    setSelectedCategoryIds([]);
  }, [selectedVersionId]);
  
  // Use the mutation hook
  const copyChannelsMutation = useCopyChannelsBetweenRegions();
  
  // Get channels for selected categories
  const channelsInSelectedCategories = copyMode === 'by-category' && selectedCategoryIds.length > 0
    ? channels.filter(channel => 
        channel.category_id && selectedCategoryIds.includes(channel.category_id)
      ).map(channel => channel.id)
    : [];
  
  // Handle the copy operation
  const handleCopy = () => {
    if (!selectedVersionId) return;
    
    let specificChannelIds: number[] | undefined;
    let copyLocal = copyMode === 'all';
    
    if (copyMode === 'selected') {
      specificChannelIds = selectedChannelIds;
      // When selecting specific channels, we copy them regardless of local status
      copyLocal = true;
    } else if (copyMode === 'by-category') {
      specificChannelIds = channelsInSelectedCategories;
      // When copying by category, we copy all channels in those categories regardless of local status
      copyLocal = true;
    } else if (copyMode === 'non-local') {
      // Only copy non-local channels, so copyLocal = false
      copyLocal = false;
    } else if (copyMode === 'local-only') {
      // For local-only, we need to fetch and filter just the local channels
      // We'll handle this through the RPC by setting copyLocal=true and passing null for specificChannelIds
      // The RPC will filter on is_local=true when copyLocal=true and specificChannelIds is null
      copyLocal = true;
      
      // The API needs a special flag to indicate we only want local channels
      // since by default, copyLocal=true means "include local channels along with others"
      // We're repurposing the specificChannelIds parameter to indicate local-only when set to an empty array
      specificChannelIds = []; // Empty array signals to only copy local channels
    }
    
    copyChannelsMutation.mutate({
      sourceVersionId: selectedVersionId,
      targetVersionId: currentVersionId,
      copyLocal,
      specificChannelIds,
      providerId
    }, {
      onSuccess: () => {
        onClose();
      }
    });
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Copy Channels from Another Region</DialogTitle>
          <DialogDescription>
            Select a source region and version to copy channels from.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Source Region */}
          <div className="space-y-2">
            <Label htmlFor="region">Source Region</Label>
            <Select
              value={selectedRegionId?.toString() || ""}
              onValueChange={(value) => setSelectedRegionId(Number(value))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a region" />
              </SelectTrigger>
              <SelectContent>
                {availableRegions.map((region) => (
                  <SelectItem key={region.id} value={region.id.toString()}>
                    {region.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Source Version */}
          {selectedRegionId && (
            <div className="space-y-2">
              <Label htmlFor="version">Source Version</Label>
              <Select
                value={selectedVersionId?.toString() || ""}
                onValueChange={(value) => setSelectedVersionId(Number(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a version" />
                </SelectTrigger>
                <SelectContent>
                  {versions.map((version) => (
                    <SelectItem key={version.id} value={version.id.toString()}>
                      {version.version_number} ({version.status})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* Copy Options */}
          {selectedVersionId && (
            <div className="space-y-2">
              <Label>Copy Options</Label>
              <RadioGroup
                value={copyMode}
                onValueChange={(value) => setCopyMode(value as 'all' | 'non-local' | 'local-only' | 'selected' | 'by-category')}
                className="space-y-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="all" id="all" />
                  <Label htmlFor="all">Copy all channels</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="non-local" id="non-local" />
                  <Label htmlFor="non-local">Copy non-local channels only</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="local-only" id="local-only" />
                  <Label htmlFor="local-only">Copy local channels only</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="by-category" id="by-category" />
                  <Label htmlFor="by-category">Copy channels by categories</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="selected" id="selected" />
                  <Label htmlFor="selected">Copy selected channels</Label>
                </div>
              </RadioGroup>
              
              {/* Categories Selection */}
              {copyMode === 'by-category' && (
                <div className="mt-4 space-y-2">
                  <Label>Select Categories</Label>
                  <div className="border rounded-md p-2 max-h-40 overflow-y-auto">
                    {isLoadingCategories ? (
                      <div className="flex justify-center items-center h-20">
                        <Loader2 className="h-6 w-6 animate-spin" />
                      </div>
                    ) : categories && categories.length > 0 ? (
                      <div className="space-y-2">
                        {categories.map((category) => (
                          <div key={category.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`category-${category.id}`}
                              checked={selectedCategoryIds.includes(category.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedCategoryIds(prev => [...prev, category.id]);
                                } else {
                                  setSelectedCategoryIds(prev => prev.filter(id => id !== category.id));
                                }
                              }}
                            />
                            <Label htmlFor={`category-${category.id}`}>
                              {category.name}
                            </Label>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-2 text-sm text-muted-foreground">
                        No categories found
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {channelsInSelectedCategories.length} channels will be copied from the selected categories
                  </div>
                </div>
              )}
              
              {/* Channel Selection */}
              {copyMode === 'selected' && (
                <div className="mt-4 space-y-2">
                  <Label>Select Channels</Label>
                  <div className="border rounded-md p-2 max-h-40 overflow-y-auto">
                    {isLoadingChannels ? (
                      <div className="flex justify-center items-center h-20">
                        <Loader2 className="h-6 w-6 animate-spin" />
                      </div>
                    ) : channels.length > 0 ? (
                      <div className="space-y-2">
                        {channels.map((channel) => (
                          <div key={channel.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`channel-${channel.id}`}
                              checked={selectedChannelIds.includes(channel.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedChannelIds(prev => [...prev, channel.id]);
                                } else {
                                  setSelectedChannelIds(prev => prev.filter(id => id !== channel.id));
                                }
                              }}
                            />
                            <Label htmlFor={`channel-${channel.id}`} className="flex items-center gap-2">
                              {channel.name}
                              {channel.is_local && (
                                <span className="text-xs bg-primary/20 text-primary px-1 py-0.5 rounded">Local</span>
                              )}
                            </Label>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-2 text-sm text-muted-foreground">
                        No channels found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCopy}
            disabled={
              !selectedVersionId || 
              copyChannelsMutation.isPending || 
              (copyMode === 'selected' && selectedChannelIds.length === 0) ||
              (copyMode === 'by-category' && selectedCategoryIds.length === 0)
            }
          >
            {copyChannelsMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Copying...
              </>
            ) : (
              "Copy Channels"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 